import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  User, 
  Grid3x3, 
  CheckCircle,
  DollarSign,
  Users,
  Ticket,
  FileText,
  X
} from 'lucide-react';
import { User as UserType } from '../../services/authService';

interface SidebarProps {
  user: UserType;
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, isOpen, onClose }) => {
  const location = useLocation();

  const navigation = [
    { name: 'Profile', href: '/profile', icon: User, show: true },
    { name: 'Apps', href: '/apps', icon: Grid3x3, show: true },
    { name: 'Services', href: '/services', icon: CheckCircle, show: user?.auth?.admin_group || false },
    { name: 'Support Tickets', href: '/tickets', icon: Ticket, show: true },
    { name: 'Documents', href: '/documents', icon: FileText, show: true },
    { name: 'Billing', href: '/billing', icon: DollarSign, show: user?.auth?.billing_group || false },
    { name: 'Users', href: '/users', icon: Users, show: user?.auth?.admin_group || false },
  ].filter(item => item.show);

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 lg:hidden z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 flex flex-col bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:sticky lg:top-0 lg:z-30 lg:h-screen lg:flex-shrink-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <img
              src="/logoBlack.png"
              alt="ITP360 Logo"
              className="h-8 w-auto"
            />
          </div>
          <button
            onClick={onClose}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-6 px-3 flex-1 min-h-0 overflow-y-auto">
          <ul className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                      ${isActive
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                    onClick={onClose}
                  >
                    <item.icon 
                      className={`
                        mr-3 h-5 w-5 transition-colors
                        ${isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}
                      `} 
                    />
                    {item.name}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Copyright */}
        <div className="mt-auto p-4 flex-shrink-0">
          <div className="bg-gray-900 rounded-lg p-4 text-center">
            <p className="text-xs text-gray-300">
              <strong>ITP</strong> © {new Date().getFullYear()} All Rights Reserved
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
